import { useState } from 'react';
import { FiFile, FiDownload, FiAlertCircle } from 'react-icons/fi';
import { CHAT_BASE_URL } from '../../services/chatApi';
import { cn } from '../../utils/helpers';

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Resolve an attachment URL against the chat service.
 *
 * The server returns an absolute `/chat/api/...` path, which is already correct
 * same-origin. Only when VITE_CHAT_URL points somewhere else does it need
 * rebasing.
 */
function attachmentUrl(attachment, { download = false } = {}) {
  const base = CHAT_BASE_URL === '/chat/api' ? attachment.url : `${CHAT_BASE_URL}/attachments/${attachment.id}`;
  return download ? `${base}?download=1` : base;
}

export default function AttachmentList({ attachments = [], uploading = false, progress = 0 }) {
  const [failedImages, setFailedImages] = useState({});

  if (attachments.length === 0) return null;

  return (
    <div className="mt-1.5 space-y-1.5">
      {attachments.map((attachment) => {
        const isPending = attachment.pending || uploading;
        const canPreview = attachment.inline && !isPending && !failedImages[attachment.id];

        if (canPreview) {
          return (
            <a
              key={attachment.id}
              href={attachmentUrl(attachment)}
              target="_blank"
              rel="noopener noreferrer"
              className="block max-w-[260px]"
            >
              <img
                src={attachmentUrl(attachment)}
                alt={attachment.filename}
                loading="lazy"
                onError={() => setFailedImages((prev) => ({ ...prev, [attachment.id]: true }))}
                className="rounded-[10px] border border-neutral-300 max-h-64 w-auto object-contain"
              />
            </a>
          );
        }

        return (
          <div
            key={attachment.id}
            className={cn(
              'flex items-center gap-2 p-2 rounded-[10px] bg-white border border-neutral-300 max-w-[280px]',
              isPending && 'opacity-60'
            )}
          >
            {failedImages[attachment.id] ? (
              <FiAlertCircle className="w-4 h-4 shrink-0 text-alert" />
            ) : (
              <FiFile className="w-4 h-4 shrink-0 text-primary/40" />
            )}

            <div className="min-w-0 flex-1">
              <p className="text-xs text-primary/85 truncate">{attachment.filename}</p>
              <p className="text-[11px] text-primary/40">
                {isPending ? `Uploading… ${progress}%` : formatBytes(attachment.bytes)}
              </p>
              {isPending && (
                <div className="mt-1 h-1 bg-primary/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </div>

            {!isPending && (
              <a
                href={attachmentUrl(attachment, { download: true })}
                // Non-image types are served Content-Disposition: attachment by
                // the server, so this is a download regardless of the browser.
                className="p-1.5 rounded-full text-primary/50 hover:text-primary hover:bg-highlight transition-colors shrink-0"
                aria-label={`Download ${attachment.filename}`}
              >
                <FiDownload className="w-4 h-4" />
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}
